import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ChevronDown, AlertTriangle } from 'lucide-react';

/**
 * Reusable species selector — dynamic by category slug.
 * When "Otros" is selected, shows a custom input.
 * Emits: { display_name, canonical_slug, is_custom }
 */
export default function SpeciesSelect({
  categorySlug,
  value,        // canonical_slug
  displayValue, // display_name for custom
  onChange,      // (canonical_slug, display_name, is_custom) => void
  className = '',
}) {
  const [speciesData, setSpeciesData] = useState(null);
  const [customInput, setCustomInput] = useState(displayValue || '');
  const [showCustom, setShowCustom] = useState(false);

  useEffect(() => {
    if (!categorySlug) {
      setSpeciesData(null);
      return;
    }
    api.getSpeciesBySlug(categorySlug).then(setSpeciesData).catch(() => setSpeciesData(null));
  }, [categorySlug]);

  useEffect(() => {
    if (value && speciesData?.species) {
      const match = speciesData.species.find((s) => s.canonical_slug === value);
      setShowCustom(match?.is_other || false);
    }
  }, [value, speciesData]);

  const handleSelect = (e) => {
    const slug = e.target.value;
    if (!slug) {
      onChange('', '', false);
      setShowCustom(false);
      return;
    }
    const sp = speciesData?.species?.find((s) => s.canonical_slug === slug);
    if (sp?.is_other) {
      setShowCustom(true);
      onChange(slug, '', true);
    } else {
      setShowCustom(false);
      onChange(slug, sp?.display_name || '', false);
    }
  };

  const handleCustomChange = (e) => {
    const raw = e.target.value;
    setCustomInput(raw);
    // Title Case on display
    const display = raw.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    onChange('custom', display, true);
  };

  if (!categorySlug) return null;

  const isHighSensitivity = speciesData?.sensitivity === 'high';

  return (
    <div className={className} data-testid="species-select">
      <Label className="text-zinc-400 mb-2 block">Especie</Label>
      {isHighSensitivity && (
        <div className="mb-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="veneno-warning">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <span className="text-xs text-red-300">Categoria de alta sensibilidad. Se requiere documentacion estricta y revision manual.</span>
        </div>
      )}
      <div className="relative">
        <select
          value={showCustom ? (speciesData?.species?.find(s => s.is_other)?.canonical_slug || '') : (value || '')}
          onChange={handleSelect}
          className="w-full bg-white/5 border border-white/10 text-zinc-100 rounded-lg px-4 py-2.5 pr-10 appearance-none focus:outline-none focus:border-emerald-500/50"
          data-testid="species-select-dropdown"
        >
          <option value="" className="bg-zinc-900">Seleccionar especie...</option>
          {speciesData?.species?.map((sp) => (
            <option key={sp.canonical_slug} value={sp.canonical_slug} className="bg-zinc-900">
              {sp.display_name}
              {sp.user_generated ? ' *' : ''}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-zinc-500 pointer-events-none" />
      </div>

      {showCustom && (
        <div className="mt-2">
          <Input
            value={customInput}
            onChange={handleCustomChange}
            placeholder="Nombre de la especie..."
            className="bg-white/5 border-white/10 text-zinc-100"
            data-testid="species-custom-input"
          />
          <p className="text-xs text-zinc-500 mt-1">Se normalizara a Title Case automaticamente</p>
        </div>
      )}
    </div>
  );
}
